import type { ViewComponent } from "@frui.ts/views";
import { registerView } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import type { ListColumnDefinition } from "views/common/list";
import ListView from "views/common/list";
import React from "react";
import Button from "@edaz/edazkit/Button";
import PaymentCardGatewayViewModel from "viewModels/finance/payments/cardGateWay/paymentCardGatewayViewModel";
import type CardGatewayPayment from "entities/cardGatewayPaymentResponse";
import PaymentCardGatewayFilters from "./filters/cardGatewayFilters";
import { formatAmount, formatDate, formatDateTime } from "utils/localizations";
import { emptyValue } from "utils/emptyValue";

const PaymentCardGatewayView: ViewComponent<PaymentCardGatewayViewModel> = observer(({ vm }) => {
  const headerClassName = "py-4 align-middle";
  const wrapClassName = "text-wrap";
  const onItemClick = vm.onItemClick();

  const columns: ListColumnDefinition<CardGatewayPayment>[] = [
    {
      property: "statementDate",
      title: vm.translateAttribute("statementDate"),
      headerClassName: `${headerClassName} ${wrapClassName}`,
      cellClassName: wrapClassName,
      valueFormatter: props => formatDate(props.value),
    },
    {
      property: "transactionType",
      headerClassName: `${headerClassName}`,
      cellClassName: wrapClassName,
      cellProps: _ => ({ style: { minWidth: 150 } }),
      title: vm.translateAttribute("transactionType"),
      valueFormatter: props => vm.translate(props.value) ?? emptyValue,
    },
    {
      property: "bruttoAmount",
      headerClassName: `${headerClassName}`,
      title: vm.translateAttribute("bruttoAmount"),
      cellProps: _ => ({ style: { color: _.value > 0 ? "" : "#c3113c" } }),
      valueFormatter: props => formatAmount(props.value),
    },
    {
      property: "paymentClassification",
      headerClassName: `${headerClassName} ${wrapClassName} text-left`,
      cellClassName: wrapClassName,
      title: vm.translateAttribute("paymentClassification"),
      cellProps: _ => ({ style: { minWidth: 300 } }),
      valueFormatter: props => (props.value ? vm.translatePaymentTemplate(props.value) : emptyValue),
    },
    {
      property: "variableSymbol",
      headerClassName: `${headerClassName} ${wrapClassName} text-right`,
      title: vm.translateAttribute("variableSymbol"),
      valueFormatter: props => props.value ?? emptyValue,
    },
    {
      property: "cardNumber",
      headerClassName: `${headerClassName} ${wrapClassName}`,
      title: vm.translateAttribute("cardNumber"),
      valueFormatter: props => props.value ?? emptyValue,
    },
    {
      property: "authCode",
      headerClassName: `${headerClassName} ${wrapClassName}`,
      title: vm.translateAttribute("authCode"),
      valueFormatter: props => props.value ?? emptyValue,
    },
    {
      property: "arn",
      headerClassName: `${headerClassName}`,
      title: vm.translateAttribute("arn"),
      valueFormatter: props => props.value ?? emptyValue,
    },
    {
      property: "paymentIdent",
      headerClassName: `${headerClassName}`,
      title: vm.translateAttribute("paymentIdent"),
      valueFormatter: props => props.value ?? emptyValue,
    },
    {
      property: "authorizedAt",
      headerClassName: `${headerClassName} ${wrapClassName} text-left`,
      cellClassName: wrapClassName,
      title: vm.translateAttribute("authorizedAt"),
      valueFormatter: props => (props.value ? formatDateTime(props.value) : emptyValue),
    },
  ];

  let actionButtons;
  if (onItemClick) {
    actionButtons = (item: CardGatewayPayment) => (
      <Button size="sm" className="rounded btn btn-success btn-sm" onClick={() => onItemClick(item)}>
        {vm.translate("detail")}
      </Button>
    );
  }

  return (
    <>
      <PaymentCardGatewayFilters vm={vm} />
      <ListView
        showDetailWithList={true}
        vm={vm}
        columns={columns}
        hideTitle={true}
        mandatoryFilter={true}
        actionButtons={actionButtons}
      />
    </>
  );
});

registerView(PaymentCardGatewayView, PaymentCardGatewayViewModel);
